import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';

export interface UserInfo {
  username?: string;
  email?: string;
  groups?: string[];
  status_code?: number;
  new_login?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserStateService {
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private isInitializedSubject = new BehaviorSubject<boolean>(false);
  
  public userInfo$ = this.userInfoSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public isInitialized$ = this.isInitializedSubject.asObservable();

  constructor(private router: Router) {}

  /**
   * Load user information from the backend
   * This method caches the result to avoid repeated API calls
   */
  async loadUserInfo(forceRefresh: boolean = false): Promise<UserInfo> {
    // If we already have user info and not forcing refresh, return cached data
    if (!forceRefresh && this.userInfoSubject.value && 'username' in this.userInfoSubject.value) {
      return this.userInfoSubject.value;
    }

    // If already loading, wait for the current load to complete
    if (this.isLoadingSubject.value && !forceRefresh) {
      return firstValueFrom(
        this.userInfo$.pipe(
          filter((userInfo): userInfo is UserInfo => userInfo !== null),
          take(1)
        )
      );
    }

    this.isLoadingSubject.next(true);

    try {
      const url = 'get_user_status';
      const response = await fetch(url);
      const data = await response.json();
      
      const userInfo: UserInfo = {
        status_code: response.status,
        ...data,
      };

      this.userInfoSubject.next(userInfo);
      this.isInitializedSubject.next(true);
      
      return userInfo;
    } catch (error) {
      console.error('Error loading user info:', error);
      const errorInfo: UserInfo = { status_code: 500 };
      this.userInfoSubject.next(errorInfo);
      this.isInitializedSubject.next(true);
      return errorInfo;
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  /**
   * Get current user info synchronously (returns cached value)
   */
  getCurrentUserInfo(): UserInfo | null {
    return this.userInfoSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const userInfo = this.userInfoSubject.value;
    return userInfo !== null && 'username' in userInfo;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const userInfo = this.userInfoSubject.value;
    return userInfo?.groups?.includes(role) ?? false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const userInfo = this.userInfoSubject.value;
    return roles.some(role => userInfo?.groups?.includes(role)) ?? false;
  }

  /**
   * Get the default route for the current user based on their role
   */
  getDefaultRouteForUser(): string {
    const userInfo = this.userInfoSubject.value;
    
    if (!userInfo || !('username' in userInfo)) {
      return '/account';
    }

    const groups = userInfo.groups || [];

    // Priority order: solution_manager > sales_rep > emplay_consultant > customer_consultant
    if (groups.includes('solution_manager')) {
      return '/home/solutions-manager/marketplace';
    } else if (groups.includes('emplay_consultant')) {
      return '/home/emplay';
    } else if (groups.includes('customer_consultant')) {
      return '/home/customer';
    } else if (groups.includes('sales_rep')) {
      return '/home/sales-rep';
    } else {
      // Default fallback
      return '/home/settings/documentation';
    }
  }

  /**
   * Navigate to the appropriate route based on user role
   */
  navigateToDefaultRoute(): void {
    const defaultRoute = this.getDefaultRouteForUser();
    this.router.navigate([defaultRoute]);
  }

  /**
   * Clear user info (for logout)
   */
  clearUserInfo(): void {
    this.userInfoSubject.next(null);
    this.isInitializedSubject.next(false);
  }

  /**
   * Wait for initialization to complete
   */
  async waitForInitialization(): Promise<void> {
    if (this.isInitializedSubject.value) {
      return;
    }
    
    return firstValueFrom(
      new Observable<void>(observer => {
        const subscription = this.isInitialized$.subscribe(isInitialized => {
          if (isInitialized) {
            observer.next();
            observer.complete();
          }
        });
        
        return () => subscription.unsubscribe();
      })
    );
  }
}
